import { removeTrapFocus, trapFocus, PUB_SUB_EVENTS, subscribe } from '../storefront/globals';

export default class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.gwpManuallyRemoved = false;

    document.addEventListener('cart:open', () => {
      this.open();
    });

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close(),
    );

    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this),
    );

    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    document.addEventListener('product:added', () => {
      this.refreshCart();
    });

    document.addEventListener('quizKitAddToCartSuccess', () => {
      this.refreshCart();
    });

    // Subscribe to cart update events
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      this.handleCartUpdate();
    });

    // Listen for click events on the cart drawer
    this.addEventListener('click', (event) => {
      this.handleRemoveButtonClick(event);
    });
  }

  handleRemoveButtonClick(event) {
    const storedGwpVariantId = localStorage.getItem('gwpVariantId');  // Retrieve from localStorage
    if (!storedGwpVariantId) return;

    let removeButton = event.target;
    if (removeButton?.tagName !== 'BUTTON') {
        removeButton = event.target.closest('quantity-input');
        removeButton = removeButton?.querySelector('button');
    }

    if (removeButton) {
        const variantId = removeButton.getAttribute('data-variant-id');
        if (variantId === storedGwpVariantId) {
            this.gwpManuallyRemoved = true;
        }
    }
  }

  handleCartUpdate() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cartData => {
        const gwpItem = cartData.items.find(item => item.variant_id === this.gwpVariantId);
        if (!gwpItem && !this.gwpManuallyRemoved) {
          // The GWP was removed automatically due to conditions, not manually
          this.gwpManuallyRemoved = false;
        }
        this.refreshCart();
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    setTimeout(() => {
      this.setAttribute('open', '');
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.js-cart-drawer-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.js-cart-drawer-close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.removeAttribute('open');
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    const cartDrawerEl = this.querySelector('cart-drawer-note');
    if (cartDrawerEl) {
      cartDrawerEl.closeFromOutside();
    }

    document.body.classList.remove('overflow-hidden');

    setTimeout(() => {
      if (window.cartTriggeredBy) {
        window.cartTriggeredBy.focus();
        window.cartTriggeredBy = null;
      }
    }, 50);
  }

  renderContents(parsedState, openDrawer = true) {
    const drawerInnerEl = this.querySelector('.drawer__inner');

    if (drawerInnerEl.classList.contains('is-empty')) {
      drawerInnerEl.classList.remove('is-empty');
    }

    this.productId = parsedState.id || '';

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const dom = this.getSectionDOM(
        parsedState.sections[section.id],
        section.selector,
      );

      sectionElement.innerHTML = dom.innerHTML;

      if (section.id === 'cart-drawer') {
        const cartData = dom.querySelector('[data-cart-data]');

        if (cartData.textContent) {
          this.initGWP(JSON.parse(cartData.textContent));
        }
      }
    });

    if (!openDrawer) return;

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener(
        'click',
        this.close.bind(this),
      );
      this.open();
    }, 50);
  }

  refreshCart(openDrawer = true) {
    // Custom function comes from Add Product CTAs
    if (this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
    }

    const sectionToRender = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    fetch(`/?sections=${sectionToRender}`)
      .then((response) => response.json())
      .then((response) => {
        this.renderContents(
          {
            sections: response,
          },
          openDrawer,
        );
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Start GWP
   */
  initGWP(cartData) {
    const {
      cart,
      gwp,
      gwp_variant_id: gwpVariantId,
      gwp_hash: gwpHash,
      customer_gets_gwp_variant_id: customerGetsGwpVariantId,
      trigger_product_variant_id: triggerProductId,
    } = cartData;

    localStorage.setItem('gwpVariantId', gwpVariantId);
    if (!cart || !gwp || !gwpHash) return;

    const cartDrawerItems = document.querySelector('cart-drawer-items');
    const cartGwpItems = cart.items.filter((line) => line.properties?._gwp);
    const cartSubtotal = this.calculateCartTotalWithGWP(
      cart.items.filter((line) => !line.properties?._gwp),
    );

    const productsToAdd = [];
    const productsToRemove = [];

    // Check for trigger product in the cart
    const customerBuysGwpVariantId = cart.items.some(
      (line) => line.variant_id === triggerProductId
    );

    // Determine the GWP variant ID to use
    const gwpVariantToAdd = customerBuysGwpVariantId
      ? customerGetsGwpVariantId
      : gwpVariantId;

    const gwpItem = cartGwpItems.find(
      (line) => line.variant_id === gwpVariantToAdd,
    );

    if (this.gwpManuallyRemoved) {
      return;
    }

    if (cartSubtotal >= gwp.cart_subtotal_minimum || customerBuysGwpVariantId) {
      if (!gwpItem) {
        productsToAdd.push({
          id: `${gwpVariantToAdd}`,
          properties: {
            _gwp: `${gwpVariantToAdd}`,
            _gwp_guid: `${gwpHash}`,
            _isProductGwp: true
          },
        });
      }
    } else if (gwpItem) {
      productsToRemove.push(gwpItem.key);
    }

    if (productsToAdd.length) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: productsToAdd }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then((productid) => {
          // Dispatch the 'product:added' event with details
          document.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                variant: productid,
              },
            }),
          );
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error:', error);
        });
    }

    if (productsToRemove.length) {
      const gwpLineIndex = cartDrawerItems
        .querySelector(`[data-variant-id="${gwpVariantToAdd}"]`)
        ?.closest('cart-remove-button').dataset.index;

      if (!gwpLineIndex) return;

      cartDrawerItems.updateQuantity(gwpLineIndex, 0);
    }
  }

  calculateCartTotalWithGWP(cartItems) {
    let cartTotal = 0;

    cartItems.forEach((item) => {
      if (item.properties?._gwp) return;
      cartTotal += item.final_line_price;
    });
    const finalTotal = cartTotal > 0 ? cartTotal / 100 : 0;
    this.updateSubTotal(finalTotal);
    return finalTotal;
  }

  updateSubTotal(total) {
    if (total || total === 0) {
      document.querySelector('[data-cart-subtotal]').textContent = `$${total}`;
    } else {
      const originalCartData = document.querySelector('[data-cart-data]');
      const parsedCartData = originalCartData
        ? JSON.parse(originalCartData.textContent)
        : null;

      if (!parsedCartData) return;
      this.calculateCartTotalWithGWP(parsedCartData.items);
    }
  }
  /**
   * End GWP
   */
}

customElements.define('cart-drawer', CartDrawer);